// src/components/exam/PracticeQuestion.tsx
'use client'

import { useState } from 'react'
import { SafeHtml } from '@/lib/utils/safe-html'
import { Button } from '@/components/ui/button'
import { CheckCircle2, XCircle, Lightbulb } from 'lucide-react'

interface PracticeOption {
  id: string
  text: string
  optionKey: string
  isCorrect: boolean
  imageUrl: string | null
}

interface PracticeQuestionProps {
  question: {
    id: string
    statement: string
    imageUrl: string | null
    explanation: string | null
    type: string
    matchPairs?: any
    correctAnswerExact?: number | null
    correctAnswerMin?: number | null
    correctAnswerMax?: number | null
    options: PracticeOption[]
  }
  questionNumber: number
  onAnswered: (isCorrect: boolean) => void // called once when user checks
}

// ... skipped left labels ...

// ✅ renders Column I / Column II for match type questions
const LEFT_LABELS = ["A", "B", "C", "D", "E", "F"];
const RIGHT_LABELS = ["i", "ii", "iii", "iv", "v", "vi"];

function MatchTable({ matchPairs }: { matchPairs: any }) {
  if (!matchPairs || !matchPairs.leftColumn || !matchPairs.rightColumn) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 my-4 bg-gray-50/50 dark:bg-gray-900/50">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100/50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
            <th className="text-left px-4 py-2.5 font-medium w-1/2 text-gray-700 dark:text-gray-300">
              {matchPairs.leftColumn.header}
            </th>
            <th className="text-left px-4 py-2.5 font-medium w-1/2 text-gray-700 dark:text-gray-300">
              {matchPairs.rightColumn.header}
            </th>
          </tr>
        </thead>
        <tbody>
          {matchPairs.leftColumn.items.map((leftItem: string, index: number) => (
            <tr key={index} className="border-b border-gray-200 dark:border-gray-800 last:border-0">
              <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-300">
                <div className="flex items-start gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-400 text-xs font-semibold shrink-0 mt-0.5">
                    {LEFT_LABELS[index]}
                  </span>
                  <SafeHtml html={leftItem} className="inline" />
                </div>
              </td>
              <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-300">
                <div className="flex items-start gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs font-semibold shrink-0 mt-0.5">
                    {RIGHT_LABELS[index]}
                  </span>
                  <SafeHtml
                    html={matchPairs.rightColumn.items[index]}
                    className="inline"
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function PracticeQuestion({
  question,
  questionNumber,
  onAnswered,
}: PracticeQuestionProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [natAnswer, setNatAnswer] = useState<string>('')
  const [isChecked, setIsChecked] = useState(false)
  const [isCorrectAnswer, setIsCorrectAnswer] = useState(false)

  const handleCheck = () => {
    let correct = false
    if (question.type === 'numerical') {
      if (!natAnswer.trim() || isChecked) return
      const val = parseFloat(natAnswer)
      if (!isNaN(val)) {
        if (question.correctAnswerExact !== null && question.correctAnswerExact !== undefined) {
          correct = val === question.correctAnswerExact
        } else if (question.correctAnswerMin !== null && question.correctAnswerMax !== null) {
          correct = val >= question.correctAnswerMin! && val <= question.correctAnswerMax!
        }
      }
    } else {
      if (!selectedKey || isChecked) return
      correct = !!question.options.find(o => o.optionKey === selectedKey)?.isCorrect
    }
    
    setIsChecked(true)
    setIsCorrectAnswer(correct)
    onAnswered(correct)
  }

  const getOptionStyle = (option: PracticeOption) => {
    if (!isChecked) {
      // Before check — highlight selected only
      return selectedKey === option.optionKey
        ? 'border-primary bg-primary/5'
        : 'border-border hover:bg-muted/50 cursor-pointer'
    }
    // After check — colour-code all relevant options
    if (option.isCorrect) {
      return 'border-green-500 bg-green-50 dark:bg-green-950/30'
    }
    if (selectedKey === option.optionKey && !option.isCorrect) {
      return 'border-red-500 bg-red-50 dark:bg-red-950/30'
    }
    return 'border-border opacity-50'
  }

  const getOptionIcon = (option: PracticeOption) => {
    if (!isChecked) return null
    if (option.isCorrect) {
      return <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
    }
    if (selectedKey === option.optionKey && !option.isCorrect) {
      return <XCircle className="w-5 h-5 text-red-500 shrink-0" />
    }
    return null
  }

  return (
    <div id={`question-${question.id}`} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-5">

      {/* Question header */}
      <div className="flex items-start gap-3">
        <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 text-sm font-bold">
          {questionNumber}
        </span>
        <div className="flex-1 text-base leading-relaxed text-gray-900 dark:text-white">
          <SafeHtml html={question.statement} />
        </div>
      </div>

      {/* Question image (if any) */}
      {question.imageUrl && (
        <div className="ml-11">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={question.imageUrl}
            alt={`Question ${questionNumber} image`}
            className="max-h-64 rounded-lg border border-gray-200 dark:border-gray-700 object-contain"
          />
        </div>
      )}

      {/* Match columns (if any) */}
      {(question.type === 'MATCH' || question.type === 'match') && question.matchPairs && (
        <div className="ml-11">
          <MatchTable matchPairs={question.matchPairs} />
        </div>
      )}

      {/* Options or NAT Input */}
      {question.type === 'numerical' ? (
        <div className="ml-11 space-y-2.5">
          <div className="flex items-center gap-3">
            <input
              type="number"
              step="any"
              value={natAnswer}
              onChange={(e) => !isChecked && setNatAnswer(e.target.value)}
              placeholder="Enter numerical answer"
              disabled={isChecked}
              className="flex h-10 w-full md:w-64 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          {isChecked && (
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-2">
              Correct Answer:{' '}
              <span className="text-blue-600 dark:text-blue-400">
                {question.correctAnswerExact !== null && question.correctAnswerExact !== undefined 
                  ? question.correctAnswerExact 
                  : `${question.correctAnswerMin} to ${question.correctAnswerMax}`}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="ml-11 space-y-2.5">
          {question.options.map((option) => (
            <div
              key={option.id}
              onClick={() => !isChecked && setSelectedKey(option.optionKey)}
              className={`flex items-center gap-3 p-3.5 rounded-lg border-2 transition-all duration-200 select-none ${getOptionStyle(option)} ${
                isChecked ? 'cursor-default' : ''
              }`}
            >
              {/* Option key pill */}
              <span
                className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-colors ${
                  !isChecked && selectedKey === option.optionKey
                    ? 'bg-primary text-primary-foreground'
                    : isChecked && option.isCorrect
                    ? 'bg-green-500 text-white'
                    : isChecked && selectedKey === option.optionKey && !option.isCorrect
                    ? 'bg-red-500 text-white'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {option.optionKey}
              </span>

              <div className="flex-1 text-sm leading-relaxed">
                <SafeHtml html={option.text} className="inline" />
                {option.imageUrl && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={option.imageUrl}
                    alt=""
                    className="mt-2 max-h-24 rounded object-contain"
                  />
                )}
              </div>

              {/* Result icon — right side */}
              {getOptionIcon(option)}
            </div>
          ))}
        </div>
      )}

      {/* Check Answer button + result feedback */}
      <div className="ml-11 space-y-3">
        {!isChecked ? (
          <Button
            onClick={handleCheck}
            disabled={question.type === 'numerical' ? !natAnswer.trim() : !selectedKey}
            className="w-full sm:w-auto"
          >
            Check Answer
          </Button>
        ) : (
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
              isCorrectAnswer
                ? 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400'
                : 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400'
            }`}
          >
            {isCorrectAnswer ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Correct!
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4" />
                Incorrect
              </>
            )}
          </div>
        )}

        {/* Explanation — only shown after check */}
        {isChecked && question.explanation && (
          <div className="flex gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
            <Lightbulb className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
              <SafeHtml html={question.explanation} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}